import React, { useEffect, useState } from 'react';
import { isInViewport } from '../../utils/common';
import { UitkLayoutFlex } from 'uitk-react-layout-flex';
import { UitkSpacing } from 'uitk-react-spacing';
import { UitkText } from 'uitk-react-text';
import useLocale from '../../hooks/useLocale';
import throttle from 'lodash.throttle';
import useNavigationParams from '../../hooks/useNavigationParams';

interface Heading {
  id: string;
  level: number;
  text: string;
  element: HTMLHeadingElement;
  tocLinkId: string;
  order: number;
}

const linkSpacingMap: Map<number, string> = new Map([
  [1, 'unset'],
  [2, 'unset'],
  [3, '0.7rem'],
  [4, '1.4rem'],
  [5, '2.1rem'],
  [6, '2.8rem'],
]);

const TableOfContent = ({ contentVersion = 0 }) => {
  const [headings, setHeadings] = useState<Heading[]>([]);
  const { formatContent } = useLocale();
  const { documentId } = useNavigationParams();

  const getAllHeadings = () => {
    const allHeadings = [];
    // We inlcude headings up to level 3, no more than that
    const htmlHeadings = document.querySelectorAll([1, 2, 3].map((level) => `h${level}.md-heading`).join(','));

    // Skip the first heading as it will always be the document title, no need to link to it
    for (let i = 1; i < htmlHeadings.length; i++) {
      const htmlHeading = htmlHeadings.item(i);
      if (htmlHeading.getBoundingClientRect().width > 0) {
        allHeadings.push({
          level: parseInt(htmlHeading.tagName.slice(1), 10),
          text: htmlHeading.textContent,
          id: htmlHeading.id,
          element: htmlHeading,
          tocLinkId: `toc-link-${htmlHeading.id}`,
        });
      }
    }
    return allHeadings;
  };

  useEffect(() => {
    const allHeadings = getAllHeadings();
    setHeadings(allHeadings);
    document.getElementById('document')?.addEventListener('scroll', () => handleScroll(allHeadings));
  }, [documentId, contentVersion]);

  useEffect(() => {
    if (headings.length > 1) {
      handleScroll(headings);
    }
  }, [headings]);

  return headings.length > 1 ? (
    <div>
      <UitkSpacing padding={{ inline: 'six' }}>
        <UitkLayoutFlex direction="column" space="two" className="table-of-content">
          <div>
            <UitkText size={200}>{formatContent('on-this-page')}</UitkText>
          </div>
          {headings.map((heading, index) => (
            <div
              style={{ paddingInlineStart: linkSpacingMap.get(heading.level) }}
              key={`${heading.tocLinkId}-${index}`}
            >
              <a href={`#${heading.id}`} id={heading.tocLinkId}>
                <UitkText theme="default" className="toc-item">
                  {heading.text}
                </UitkText>
              </a>
            </div>
          ))}
        </UitkLayoutFlex>
      </UitkSpacing>
    </div>
  ) : null;
};

const handleScroll = throttle((headings: Heading[]) => {
  const currentlyHighlightedElement = headings.filter((heading) => {
    document.getElementById(heading.tocLinkId)?.classList?.contains('toc-active-link');
  })[0];

  // Highlight only the first link from the ones visible on the screen
  let firstHeadingHighlighted = false;
  headings.map((heading) => {
    if (isInViewport(heading.element) && !firstHeadingHighlighted) {
      firstHeadingHighlighted = true;
      setHeadingHighlight(heading, true);
    } else {
      setHeadingHighlight(heading, false);
    }
  });

  // If no heading element is being displayed on the screen, keep the last active ToC link
  if (!firstHeadingHighlighted && currentlyHighlightedElement) {
    setHeadingHighlight(currentlyHighlightedElement, true);
  }
}, 20);

const setHeadingHighlight = (heading: Heading, isHighlighted: boolean) => {
  const linkElement = document.getElementById(heading?.tocLinkId);
  if (!linkElement) {
    return;
  }
  if (isHighlighted) {
    linkElement.scrollIntoView({
      behavior: 'auto',
      block: 'nearest',
      inline: 'start',
    });
    if (linkElement.classList) {
      linkElement.classList.add('toc-active-link');
    } else {
      linkElement.className = 'toc-active-link';
    }
  } else {
    linkElement.classList.remove('toc-active-link');
  }
};

export default TableOfContent;
